/**
 * Theme Variant Configuration
 * 
 * Defines all available theme variants for light and dark modes.
 * Each variant contains a complete color palette that MUI's createTheme() consumes.
 * 
 * IMPORTANT: The 'default' variant in each mode contains the EXACT colors 
 * from the original getDesignTokens() in index.js. Changing these values 
 * will alter the current production appearance.
 */

export const THEME_VARIANTS = {
  light: {
    default: {
      label: 'Default',
      palette: {
        primary: {
          main: '#1976d2',
          light: '#4791db',
          dark: '#115293'
        },
        background: {
          default: '#f4f4f4',
          paper: '#ffffff',
          header: '#ffffff'
        },
        text: {
          primary: '#000000',
          secondary: '#333333',
          header: '#000000'
        },
        buy: {
          main: '#4CAF50',
          light: '#81c784',
          dark: '#388e3c',
          background: '#0FC18C1F'
        },
        sell: {
          main: '#f44336',
          light: '#e57373',
          dark: '#d32f2f',
          background: '#DE201C1F'
        }
      }
    },
    classic: {
      label: 'Classic',
      palette: {
        primary: {
          main: '#2563EB',
          light: '#60A5FA',
          dark: '#1D4ED8'
        },
        background: {
          default: '#F5F5F5',
          paper: '#FAFAFA',
          header: '#F8FAFC'
        },
        text: {
          primary: '#1A1A2E',
          secondary: '#64748B',
          header: '#0F172A'
        },
        buy: {
          main: '#16A34A',
          light: '#4ADE80',
          dark: '#15803D',
          background: '#16A34A1F'
        },
        sell: {
          main: '#DC2626',
          light: '#F87171',
          dark: '#B91C1C',
          background: '#DC26261F'
        }
      }
    },
    'soft-blue': {
      label: 'Soft Blue',
      palette: {
        primary: {
          main: '#1D4ED8',
          light: '#60A5FA',
          dark: '#1E40AF'
        },
        background: {
          default: '#F0F4F8',
          paper: '#F8FAFC',
          header: '#F0F4F8'
        },
        text: {
          primary: '#1E293B',
          secondary: '#64748B',
          header: '#1E3A8A'
        },
        buy: {
          main: '#15803D',
          light: '#4ADE80',
          dark: '#166534',
          background: '#15803D1F'
        },
        sell: {
          main: '#B91C1C',
          light: '#F87171',
          dark: '#991B1B',
          background: '#B91C1C1F'
        }
      }
    },
    'warm-paper': {
      label: 'Warm Paper',
      palette: {
        primary: {
          main: '#2563EB',
          light: '#60A5FA',
          dark: '#1D4ED8'
        },
        background: {
          default: '#FAF8F5',
          paper: '#FCFBF8',
          header: '#FCFBF8'
        },
        text: {
          primary: '#292524',
          secondary: '#78716C',
          header: '#431407'
        },
        buy: {
          main: '#15803D',
          light: '#4ADE80',
          dark: '#166534',
          background: '#15803D1F'
        },
        sell: {
          main: '#B91C1C',
          light: '#F87171',
          dark: '#991B1B',
          background: '#B91C1C1F'
        }
      }
    },
  },

  dark: {
    default: {
      label: 'Default',
      palette: {
        primary: {
          main: '#00FFD1',
          light: '#69FFDB',
          dark: '#00BFA5'
        },
        background: {
          default: '#0F1429',
          paper: '#1A2138',
          header: 'linear-gradient(90deg, #0A1929 0%, #132f4c 100%)'
        },
        text: {
          primary: '#E0F7FA',
          secondary: '#B0BEC5',
          header: '#ffffff'
        },
        buy: {
          main: '#2E7D32',
          light: '#4CAF50',
          dark: '#1B5E20',
          background: '#0FC18C1F'
        },
        sell: {
          main: '#B71C1C',
          light: '#D32F2F',
          dark: '#8B0000',
          background: '#DE201C1F'
        }
      }
    },
    classic: {
      label: 'Classic Dark',
      palette: {
        primary: {
          main: '#60A5FA',
          light: '#93C5FD',
          dark: '#3B82F6'
        },
        background: {
          default: '#111111',
          paper: '#1A1A1A',
          header: '#09090B'
        },
        text: {
          primary: '#E4E4E7',
          secondary: '#A1A1AA',
          header: '#f4f4f5'
        },
        buy: {
          main: '#4ADE80',
          light: '#86EFAC',
          dark: '#22C55E',
          background: '#4ADE801F'
        },
        sell: {
          main: '#F87171',
          light: '#FCA5A5',
          dark: '#EF4444',
          background: '#F871711F'
        }
      }
    },
    charcoal: {
      label: 'Charcoal',
      palette: {
        primary: {
          main: '#5E9EFF',
          light: '#93C5FD',
          dark: '#3B82F6'
        },
        background: {
          default: '#1C1C1E',
          paper: '#2C2C2E',
          header: '#18181A'
        },
        text: {
          primary: '#E5E5E5',
          secondary: '#8E8E93',
          header: '#E5E5E5'
        },
        buy: {
          main: '#30D158',
          light: '#63E884',
          dark: '#28A745',
          background: '#30D1581F'
        },
        sell: {
          main: '#FF453A',
          light: '#FF6961',
          dark: '#D32F2F',
          background: '#FF453A1F'
        }
      }
    },
    'deep-navy': {
      label: 'Deep Navy',
      palette: {
        primary: {
          main: '#818CF8',
          light: '#A5B4FC',
          dark: '#6366F1'
        },
        background: {
          default: '#0B1120',
          paper: '#111827',
          header: '#070D19'
        },
        text: {
          primary: '#E2E8F0',
          secondary: '#94A3B8',
          header: '#F8FAFC'
        },
        buy: {
          main: '#34D399',
          light: '#6EE7B7',
          dark: '#10B981',
          background: '#34D3991F'
        },
        sell: {
          main: '#FB7185',
          light: '#FDA4AF',
          dark: '#F43F5E',
          background: '#FB71851F'
        }
      }
    },
  }
};

/**
 * Get the variant config for a given mode and variant key.
 * Falls back to 'default' if the variant key is not found.
 * 
 * @param {'light'|'dark'} mode 
 * @param {string} variant 
 * @returns {{ label: string, palette: object }}
 */
export const getVariantConfig = (mode, variant) => {
  return THEME_VARIANTS[mode]?.[variant] || THEME_VARIANTS[mode]?.default;
};

/**
 * Get the list of available variants for a given mode (for rendering the picker UI).
 * 
 * @param {'light'|'dark'} mode 
 * @returns {Array<{ key: string, label: string, previewBg: string, previewPrimary: string }>}
 */
export const getVariantsForMode = (mode) => {
  return Object.entries(THEME_VARIANTS[mode] || {}).map(([key, config]) => ({
    key,
    label: config.label,
    previewBg: config.palette.background.default,
    previewPrimary: config.palette.primary.main,
    previewText: config.palette.text.primary,
  }));
};
